import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { Grid, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@totalsoft/rocket-webapp-components-deprecated";
import { Dialog, DialogTitle, DialogActions, Button, DialogContent, Card, makeStyles } from "@material-ui/core";
import { useMutationWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { INSURANCE_DETAILS } from "../queries";
import { get, set, sequence } from "@totalsoft/react-state-lens";
import { SAVE_INSURANCES } from "../mutations";
import { isDirty } from "@totalsoft/change-tracking";
import { useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { addToast } from "components/notifications";

import insurancePageStyle from "features/products/insuranceStyles";
import { useApolloClient } from "@apollo/client";
const useStyles = makeStyles(insurancePageStyle);

function EditInsuranceDetails({ showInsurancePage, quoteId, quoteProductId, addInsurance }) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const theme = useTheme();

  const [arrowItemHeight, setArrowItemHeight] = useState();
  const matchesMD = useMediaQuery(theme.breakpoints.down("md"));
  const styleProps = useMemo(() => ({ matchesMD: matchesMD, arrowItemHeight: arrowItemHeight }), [
    matchesMD,
    arrowItemHeight,
  ]);
  const classes = useStyles(styleProps);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [assetsLens, dirtyInfo, resetAssetsLens] = useChangeTrackingLens(null);

  const { loading: loadingInsurances } = useQueryWithErrorHandling(INSURANCE_DETAILS, {
    variables: { quoteId, quoteProductId },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!data) return;
      resetAssetsLens(data.insuranceDetails);
    },
  });

  const [saveInsurances, { loading: saving }] = useMutationWithErrorHandling(SAVE_INSURANCES, {
    onCompleted: () => {
      const quoteProductCacheId = client.cache.identify({
        __typename: "QuoteProduct",
        quoteProductId: quoteProductId,
      });

      client.cache.evict({ id: quoteProductCacheId, fieldName: "productSchedule" });
      client.cache.gc();
      addToast(t("ToastNotifications.Success"), "success");
    },
  });

  const handleSaveInsurances = useCallback(async () => {
    const assets = assetsLens |> get;

    const assetInsurances = assets
      .map((asset) => ({
        quoteAssetId: asset.quoteAssetId,
        insuranceTypes: asset.insuranceTypes.map((it) => ({
          insuranceTypeId: it.insuranceTypeId,
          insuranceTypeName: it.insuranceTypeName,
          insuranceClassId: it.insuranceClassId,
          selectedConvention: it.selectedConvention,
        })),
      }))
      ?.filter((asset) => asset.insuranceTypes.length > 0);

    const input = {
      quoteProductId: quoteProductId,
      quoteId: quoteId,
      addInsurance,
      assets: assetInsurances,
    };

    await saveInsurances({
      variables: { input: input },
    });
    resetAssetsLens(assets);
  }, [addInsurance, assetsLens, quoteId, quoteProductId, resetAssetsLens, saveInsurances]);

  const dialogPaperProps = useMemo(() => ({ style: { overflowY: "visible", maxHeight: "100vh" } }), []);

  const handleClose = useCallback(() => {
    (showInsurancePage |> set)(false);
  }, [showInsurancePage]);

  const loadConventions = useCallback((insuranceType) => {
    const options = insuranceType.conventions;
    return options;
  }, []);

  const handleRefChange = (node) => {
    if (node?.clientHeight) setArrowItemHeight(node.clientHeight);
  };

  return (
    <>
      <Dialog maxWidth={"md"} fullScreen={fullScreen} fullWidth={true} open={true} PaperProps={dialogPaperProps}>
        <Grid container direction="column">
          <DialogTitle className={classes.marginTopDialogTitle}>
            <Typography variant="h4">{t("EditProduct.EditInsuranceTitle")}</Typography>
          </DialogTitle>
          <DialogContent>
            {(assetsLens |> sequence).map((asset, _) => (
              <Grid item xs={12} key={asset.quoteAssetId |> get}>
                <Card id={"assetCard" + (asset.quoteAssetId |> get)} className={classes.insuranceCard}>
                  <Grid container>
                    <Grid item xs={12} lg={2} className={classes.insuranceSection} ref={handleRefChange}>
                      <div className={classes.arrowTipItem}>
                        <Grid container direction="column" alignContent="center" alignItems="center">
                          <Grid item xs>
                            <Typography className={classes.brandName}>{asset.brandName |> get}</Typography>
                          </Grid>
                          <Grid item xs>
                            <Typography className={classes.assetName}>{asset.assetName |> get}</Typography>
                          </Grid>
                          <Grid item xs>
                            <Typography className={classes.kindOfObjectName}>
                              {asset.kindOfObjectName |> get}
                            </Typography>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>

                    {!(asset.notFoundCategories |> get) ? (
                      <Grid item xs={12} lg={10}>
                        <Grid container className={classes.insuranceType}>
                          {(asset.insuranceTypes |> sequence).map((insuranceType, insuranceIndex) => (
                            <Grid container key={insuranceIndex} spacing={2}>
                              <Grid item xs={4}>
                                <Typography>{insuranceType.insuranceTypeName |> get}</Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Grid container alignItems="center">
                                  <Grid item style={{ flex: 1 }}>
                                    <Typography className={classes.mediumFont}>
                                      {t("EditProduct.ConventionAlias")}
                                    </Typography>
                                  </Grid>
                                  <Grid item style={{ flex: 3 }}>
                                    <Autocomplete
                                      key={insuranceIndex}
                                      id={"conventionAlias_" + insuranceIndex}
                                      fullWidth
                                      cache={false}
                                      name="conventionAlias"
                                      value={insuranceType.selectedConvention |> get}
                                      valueKey="conventionId"
                                      labelKey="conventionAlias"
                                      onChange={insuranceType.selectedConvention |> set}
                                      options={loadConventions(insuranceType |> get)}
                                      isClearable={true}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid item container xs={12} lg={10} justifyContent="center" alignItems="center">
                        <Typography variant="h6" style={{ color: "orange" }}>
                          {t("Server.errCalculateInsurance")}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>
            ))}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              disabled={saving || loadingInsurances || !isDirty(dirtyInfo)}
              onClick={handleSaveInsurances}
              color="primary"
            >
              {t("Save")}
            </Button>
            <Button variant="contained" disabled={saving || loadingInsurances} onClick={handleClose} color="primary">
              {t("Close")}
            </Button>
          </DialogActions>
        </Grid>
      </Dialog>
    </>
  );
}

EditInsuranceDetails.propTypes = {
  showInsurancePage: PropTypes.object.isRequired,
  quoteId: PropTypes.number.isRequired,
  quoteProductId: PropTypes.number.isRequired,
  addInsurance: PropTypes.bool.isRequired,
};

export default EditInsuranceDetails;
